import React, { useState, useEffect, type ReactNode } from 'react'
import { Button as AntButton, Space, Tooltip, Modal } from 'antd'
import Button from '../../components/Button.jsx'
import { RecordFormDeleteEllipses } from '../../components/RecordFormDeleteEllipses.tsx'
import { RecordProvenanceTag } from '../../components/RecordProvenanceTag.tsx'
import type { AgentProvenanceEntry } from '../../../state_manager/data_models/agent_provenance_entry.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faExpand, faArrowsRotate, faCheck } from '@fortawesome/free-solid-svg-icons'
import { useDrawer } from '../../contexts/DrawerContext.jsx'
import { useTabManagerContext } from '../lib/TabManagerContext.tsx'
import { useDispatch } from 'react-redux'
import { portalColors } from '../../../portal-configs/colors.js'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { registerRecordUsage } from '../../../state_manager/state_update_actions/active_records/active_record_actions.ts'

/**
 * Enhanced RightMenu component that supports drawer functionality
 * Can display custom content or a structured drawer form
 */
export interface RightMenuProps {
  children?: ReactNode
  isVisible?: boolean
  drawerContent?: ReactNode
  drawerTitle?: string
  drawerActions?: ReactNode
  onCancel?: (() => void | Promise<void>) | null
  onSubmit?: (() => void | Promise<void>) | null
  onRefresh?: (() => void | Promise<void>) | null
  loading?: boolean
  viewFullRecord?: boolean
  /** CSS length, e.g. '35%' */
  width?: string
  showActions?: boolean
  /** Deal/record data passed from MainTabContainer */
  selectedRecord?: Record<string, unknown> | null
  /** Soft delete from the drawer: confirm text + the delete action */
  drawerDeleteConfirm?: { title: string; description?: string; onConfirm: () => Promise<void> } | null
  /**
   * Record provenance for the banner — who created this record and when.
   * System-wide slot (owner 2026-09-16): every module's drawer gets it by
   * passing this from MainTabContainer, the same way drawerDeleteConfirm is
   * passed. Null (or an entity whose detail RPC does not project the fields
   * yet) renders nothing, so it is safe to leave unset.
   */
  provenance?: {
    createdByName?: string | null
    dateCreated?: string | null
    agentProvenance?: AgentProvenanceEntry | null
  } | null
}

type CloseAction = 'cancel' | 'close' | 'navigate'

/** A string-valued field of the drawer record, or null. */
const recordText = (record: Record<string, unknown> | null, key: string): string | null => {
  const value = record ? record[key] : null
  return typeof value === 'string' && value ? value : null
}

export function RightMenu({
  children,
  isVisible = true,
  drawerContent = null,
  drawerTitle = '',
  drawerActions = null,
  onCancel = null,
  onSubmit = null,
  onRefresh = null,
  loading = false,
  viewFullRecord = false,
  width = '35%', // Default width, can be overridden
  showActions = true, // New prop to control action buttons display
  selectedRecord = null, // Deal/record data passed from MainTabContainer
  drawerDeleteConfirm = null,
  provenance = null
}: RightMenuProps) {
  const {
    isOpen,
    closeDrawer,
    isNew,
    recordId,
    basePath,
    pendingNavigation,
    confirmPendingNavigation,
    cancelPendingNavigation
  } = useDrawer()
  const { openFormInTab } = useTabManagerContext()
  const dispatch = useDispatch()
  const [confirmModalVisible, setConfirmModalVisible] = useState(false)
  const [closeAction, setCloseAction] = useState<CloseAction | null>(null)

  // Watch for pending navigation and show confirmation
  // This hook must be called before any conditional returns
  useEffect(() => {
    if (pendingNavigation && isNew) {
      setCloseAction('navigate')
      setConfirmModalVisible(true)
    }
  }, [pendingNavigation, isNew])

  if (!isVisible) return null

  // Check isOpen first to hide drawer immediately when closing
  if (!isOpen) return null

  // Determine if we should show drawer content
  const shouldShowDrawer = drawerContent

  // Handle close with confirmation for new records
  const handleClose = (actionType: CloseAction) => {
    if (isNew && !pendingNavigation) {
      setCloseAction(actionType)
      setConfirmModalVisible(true)
    } else {
      if (onCancel) {
        onCancel()
      } else {
        closeDrawer()
      }
    }
  }

  // Handle confirmed close
  const handleConfirmClose = () => {
    setConfirmModalVisible(false)

    if (closeAction === 'navigate') {
      confirmPendingNavigation()
    } else {
      // Handle cancel button or X icon close
      if (onCancel) {
        onCancel()
      } else {
        closeDrawer()
      }
    }

    setCloseAction(null)
  }

  // Handle cancel close
  const handleCancelClose = () => {
    const action = closeAction
    setConfirmModalVisible(false)
    setCloseAction(null)

    // If it was a pending navigation, cancel it
    if (action === 'navigate') {
      cancelPendingNavigation()
    }
  }

  // Handle View Full Record navigation - opens in tab instead of direct navigation
  const handleViewFullRecord = () => {
    if (basePath && recordId) {
      // Extract base module from basePath, handling compound paths like '/deals/pipeline'
      let module = basePath.replace('/', '')

      // For deal pipeline, we want to open the deal form, not a pipeline form
      if (basePath === '/deals/pipeline') {
        module = 'deals'
      }
      // For fundraising pipeline, open the prospect form in main fundraising module (same as deal pipeline)
      if (basePath === '/fundraising/pipeline') {
        module = 'fundraising'
      }

      // Register fullForm usage BEFORE navigating to prevent race condition
      // This ensures the record stays in activeRecords when drawer unmounts
      if (recordId && !isNew) {
        dispatch(registerRecordUsage({ uuid: recordId.toString(), context: 'fullForm' }))
      }

      // Determine tab title - use record name if available
      let tabTitle = `${module} ${recordId}`
      if (module === 'deals') {
        tabTitle = recordText(selectedRecord, DealFields.deals__name) ?? tabTitle
      } else if (module === 'companies' || module === 'fundraising') {
        tabTitle = recordText(selectedRecord, AccountRecordFields.accounts__name) ?? tabTitle
      }

      openFormInTab(module, recordId, tabTitle)
    }
  }

  // Default drawer actions if not provided
  const cancelButton = (
    <Button variant='cancel' onClick={() => handleClose('cancel')} size='middle'>
      Cancel
    </Button>
  )

  const defaultActions = drawerActions || (
    <Space>
      {cancelButton}
      <Button
        variant='save'
        icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
        onClick={onSubmit}
        loading={loading}
        size='middle'
      >
        {isNew ? 'Save' : 'Update'}
      </Button>
    </Space>
  )

  // Only render when drawer should be shown
  if (!shouldShowDrawer) return null

  return (
    <div
      data-debug='right-menu-container'
      data-component='RightMenu'
      data-width={width}
      data-is-new={isNew}
      className='h-full bg-white border-l border-gray-200 shadow-lg transition-all duration-300 ease-in-out'
      style={{ width }}
    >
      {/* Drawer mode - structured layout with header and actions */}
      <div data-debug='right-menu-layout' className='h-full flex flex-col'>
        {/* Drawer Header */}
        <div
          data-debug='right-menu-header'
          className='flex items-center justify-between px-4 border-b border-gray-200 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 gap-2'
          style={{ height: '48px' }}
        >
          <div data-debug='right-menu-title-section' className='flex-1 min-w-0'>
            <h3 className='text-lg font-semibold text-white truncate'>
              {drawerTitle || `${isNew ? 'New' : 'Edit'} ${recordId ? `(ID: ${recordId})` : ''}`}
            </h3>
          </div>
          <div data-debug='right-menu-actions' className='flex items-center space-x-2 flex-shrink-0'>
            {!isNew && provenance && (
              <RecordProvenanceTag
                surface='onBlue'
                createdByName={provenance.createdByName}
                dateCreated={provenance.dateCreated}
                agentProvenance={provenance.agentProvenance}
              />
            )}
            {viewFullRecord && !isNew && (
              <Tooltip title='Open in Full Form'>
                <AntButton
                  data-debug='view-full-record-btn'
                  size='small'
                  icon={<FontAwesomeIcon icon={faExpand} />}
                  onClick={handleViewFullRecord}
                  className='bg-white/20 border-white/40 text-white px-3 py-1 h-auto hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                  style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                  title='Open in Full Form'
                />
              </Tooltip>
            )}
            {viewFullRecord && !isNew && onRefresh && (
              <Tooltip title='Refresh'>
                <AntButton
                  data-debug='refresh-drawer-btn'
                  size='small'
                  icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  onClick={onRefresh}
                  loading={loading}
                  className='bg-white/20 border-white/40 text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                  style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                />
              </Tooltip>
            )}
            {viewFullRecord && !isNew && drawerDeleteConfirm && (
              <RecordFormDeleteEllipses
                key={recordId ?? 'drawer-delete'}
                variant='drawer'
                confirmTitle={drawerDeleteConfirm.title}
                confirmDescription={drawerDeleteConfirm.description}
                onDelete={drawerDeleteConfirm.onConfirm}
                loading={loading}
              />
            )}
            <Tooltip title='Close'>
              <AntButton
                data-debug='close-drawer-btn'
                size='small'
                icon={<FontAwesomeIcon icon={faTimes} />}
                onClick={() => handleClose('close')}
                className='bg-white/20 border-white/40 text-white hover:!bg-white/30 hover:!border-white/60 hover:!text-white'
                style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                title='Close'
              />
            </Tooltip>
          </div>
        </div>

        {/* Drawer Content */}
        <div data-debug='right-menu-content' className='flex-1 min-h-0 overflow-y-auto'>
          <div data-debug='right-menu-content-inner' className='p-2 h-full'>
            {drawerContent}
          </div>
        </div>

        {/* Drawer Actions */}
        {showActions && (onSubmit || defaultActions) && (
          <div
            data-debug='right-menu-footer-actions'
            className='p-2 border-t border-gray-200 bg-gray-50 flex justify-end'
          >
            {defaultActions}
          </div>
        )}

        {/* Confirmation Modal - shown when closing new record via cancel button, X icon, or clicking existing record */}
        {isNew && (
          <Modal
            title='Are you sure you want to close this new record?'
            open={confirmModalVisible}
            onCancel={handleCancelClose}
            closable={false}
            maskClosable={false}
            footer={
              <Space>
                <Button variant='cancel' onClick={handleCancelClose}>
                  No
                </Button>
                <Button
                  variant='delete'
                  onClick={handleConfirmClose}
                  style={{
                    backgroundColor: portalColors.error,
                    borderColor: portalColors.error,
                    color: '#FFFFFF'
                  }}
                  className='hover:!bg-red-600 hover:!border-red-600'
                >
                  Yes
                </Button>
              </Space>
            }
          >
            <p>
              {closeAction === 'navigate'
                ? 'You are about to open another record. Any unsaved changes will be lost.'
                : 'Any unsaved changes will be lost.'}
            </p>
          </Modal>
        )}
      </div>
    </div>
  )
}
